import { type BaseTexture, type PBRMaterial, Texture } from '@babylonjs/core';

const materialPropertiesToResetViaCloning = [
  'albedoColor',
  'reflectivityColor',
  'emissiveColor',
  'ambientColor',
] as const satisfies readonly (keyof PBRMaterial)[];

const materialPropertiesToReset = [
  // Basic material properties
  'metallic',
  'roughness',
  'backFaceCulling',

  // Alpha and transparency
  'useAlphaFromAlbedoTexture',
  'useSpecularOverAlpha',
  'useRadianceOverAlpha',
  'transparencyMode',

  // Lighting
  'disableLighting',
  'environmentIntensity',
  'useLightmapAsShadowmap',

  // Normal mapping
  'invertNormalMapX',
  'invertNormalMapY',

  // Parallax
  'useParallax',
  'useParallaxOcclusion',
  'parallaxScaleBias',
] as const satisfies readonly (keyof PBRMaterial)[];

const sheenPropertiesToReset = [
  'isEnabled',
  'intensity',
  'roughness',
  'linkSheenWithAlbedo',
  'texture',
] as const satisfies readonly (keyof PBRMaterial['sheen'])[];

const clearCoatPropertiesToReset = [
  'isEnabled',
  'intensity',
  'indexOfRefraction',
  'roughness',
] as const satisfies readonly (keyof PBRMaterial['clearCoat'])[];

const texturePropertiesToReset = [
  'level',
  'coordinatesIndex',
  'uScale',
  'vScale',
  'uOffset',
  'vOffset',
  'wAng',
  'hasAlpha',
] as const satisfies readonly (keyof Texture)[];

// Warning: this method modified nextTexture in-place!
const resetTextureProperties = (
  nextTexture: BaseTexture,
  originalTexture: BaseTexture,
): void => {
  nextTexture.level = originalTexture.level;
  nextTexture.coordinatesIndex = originalTexture.coordinatesIndex;

  if (!(originalTexture instanceof Texture && nextTexture instanceof Texture)) {
    return;
  }

  for (const property of texturePropertiesToReset) {
    // @ts-expect-error -- Not worth fixing, we know they are the same type.
    nextTexture[property] = originalTexture[property];
  }
};

/**
 * Warning: this method modified material in-place!
 *
 * Why not just clone originalMaterial? To avoid memory leaks - seems like some textures aren't cleaned up
 * after disposing of the material. See the previous versions of this file for an implementation with cloning.
 * If something seems to not be working, check if maybe another property has to be assigned from originalMaterial.
 */
export const resetMaterialProperties = (
  material: PBRMaterial,
  originalMaterial: PBRMaterial,
): void => {
  for (const property of materialPropertiesToResetViaCloning) {
    material[property] = originalMaterial[property].clone();
  }

  for (const property of materialPropertiesToReset) {
    // @ts-expect-error -- Not worth fixing, we know they are the same type.
    material[property] = originalMaterial[property];
  }

  for (const property of sheenPropertiesToReset) {
    // @ts-expect-error -- Not worth fixing, we know they are the same type.
    material.sheen[property] = originalMaterial.sheen[property];
  }

  for (const property of clearCoatPropertiesToReset) {
    // @ts-expect-error -- Not worth fixing, we know they are the same type.
    material.clearCoat[property] = originalMaterial.clearCoat[property];
  }

  material.ambientTexture = originalMaterial.ambientTexture;
  material.lightmapTexture = originalMaterial.lightmapTexture;

  if (originalMaterial.bumpTexture) {
    material.bumpTexture = originalMaterial.bumpTexture;
    resetTextureProperties(material.bumpTexture, originalMaterial.bumpTexture);
  }

  if (originalMaterial.albedoTexture) {
    material.albedoTexture = originalMaterial.albedoTexture;
    resetTextureProperties(
      material.albedoTexture,
      originalMaterial.albedoTexture,
    );
  }
};
